import React, { useState } from 'react';
import { companyApi, Company } from '../../utils/api';
import { X } from 'lucide-react';

interface Props {
    companies: Company[];
    onClose: () => void;
    /** Re-read after any write, so the user table and counts stay truthful. */
    onChanged: () => void;
}

/**
 * The customers release notes can be targeted at.
 *
 * A managed list rather than free text on each user: 'Acme', 'ACME' and
 * 'Acme Ltd' typed into three user records would be three different audiences,
 * and a note would reach one of them.
 *
 * It opens from the Users tab rather than being a tab of its own, because a
 * company is only ever useful as something to put a user in.
 */
const CompaniesDialog: React.FC<Props> = ({ companies, onClose, onChanged }) => {
    const [name, setName] = useState('');
    const [jiraValue, setJiraValue] = useState('');
    const [editingId, setEditingId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState<string | null>(null);

    const reset = () => {
        setEditingId(null);
        setName('');
        setJiraValue('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim() || !jiraValue.trim()) return;

        setBusy(true);
        setError(null);
        const dto = { name: name.trim(), jiraValue: jiraValue.trim() };
        const result = editingId
            ? await companyApi.update(editingId, dto)
            : await companyApi.create(dto);
        setBusy(false);

        if (result.error) {
            setError(result.error);
            return;
        }

        reset();
        onChanged();
    };

    /**
     * Build the list from Jira on demand.
     *
     * The same endpoint the nightly workflow calls. Without this the only ways
     * to get a company are to wait for tonight or to hand-type the Jira option
     * string exactly - and BIKI never shows that string, so it was a dead end.
     */
    const handleRefresh = async () => {
        setSyncing(true);
        setError(null);
        setSyncResult(null);
        const result = await companyApi.refreshFromJira();
        setSyncing(false);

        if (result.error) {
            setError(result.error);
            return;
        }

        // Jira answering with no options at all is refused rather than obeyed,
        // and says so instead of reporting a successful run that did nothing.
        setSyncResult(result.data?.skipped ?? result.data?.message ?? 'Done.');
        onChanged();
    };

    const handleEdit = (company: Company) => {
        setEditingId(company.id);
        setName(company.name);
        setJiraValue(company.jiraValue);
        setError(null);
    };

    const handleRemove = async (company: Company) => {
        // The API refuses this too, and says why. Asking here as well keeps a
        // company with notes on it from being removed by a stray click.
        if (!window.confirm(
            `Remove "${company.name}"?\n\n` +
            `${company.noteCount} release note(s) are targeted at it and will become ` +
            `visible to everyone.`
        )) return;

        setBusy(true);
        setError(null);
        const result = await companyApi.remove(company.id);
        setBusy(false);

        if (result.error) {
            setError(result.error);
            return;
        }

        if (editingId === company.id) reset();
        onChanged();
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 fade-in max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-slate-800">Companies</h2>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleRefresh}
                            disabled={syncing}
                            className="px-3 py-1.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium disabled:text-slate-300 disabled:cursor-not-allowed"
                        >
                            {syncing ? 'Refreshing…' : 'Refresh from Jira'}
                        </button>
                        <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <p className="text-sm text-slate-500 mb-4">
                    Release notes are targeted at a company by the nightly Jira sync, which matches
                    the <strong>Jira value</strong> below against the Customer field on each issue.
                    A note matching no company is general and everyone sees it.
                </p>
                <p className="text-sm text-slate-500 mb-4">
                    The sync also keeps this list itself up to date: companies marked{' '}
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">Jira</span>{' '}
                    are added and relabelled from the Customer field automatically. Renaming one here
                    makes the name yours and the sync stops overwriting it. Nothing is ever removed
                    automatically — a customer dropped from Jira is left in place for you to decide about.
                </p>

                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        {error}
                    </div>
                )}

                {syncResult && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                        {syncResult}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="flex flex-wrap gap-3 items-end mb-6">
                    <div className="flex-1 min-w-[10rem]">
                        <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Acme Ltd"
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            required
                        />
                    </div>
                    <div className="flex-1 min-w-[10rem]">
                        <label className="block text-sm font-medium text-slate-700 mb-1">Jira value *</label>
                        <input
                            type="text"
                            value={jiraValue}
                            onChange={(e) => setJiraValue(e.target.value)}
                            placeholder="acme"
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-mono text-sm"
                            required
                        />
                    </div>
                    <div className="flex gap-2">
                        {editingId && (
                            <button
                                type="button"
                                onClick={reset}
                                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm"
                            >
                                Cancel
                            </button>
                        )}
                        <button
                            type="submit"
                            disabled={busy}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:bg-slate-300"
                        >
                            {editingId ? 'Save' : 'Add'}
                        </button>
                    </div>
                </form>

                <div className="overflow-y-auto border border-slate-200 rounded-lg">
                    <table className="w-full">
                        <thead className="sticky top-0">
                            <tr className="bg-slate-50">
                                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                    Name
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                    Jira value
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                    Source
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                    Users
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                    Notes
                                </th>
                                <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                            {companies.map((company) => (
                                <tr key={company.id} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-4 py-3 text-sm font-medium text-slate-800">
                                        {company.name}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-500 font-mono">
                                        {company.jiraValue}
                                    </td>
                                    <td className="px-4 py-3">
                                        {company.jiraOptionId ? (
                                            <span
                                                title={`Maintained by the nightly sync (Jira option ${company.jiraOptionId})`}
                                                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800"
                                            >
                                                Jira
                                            </span>
                                        ) : (
                                            <span
                                                title="Created here. The sync adopts it if a Jira option matches its Jira value."
                                                className="text-xs text-slate-400"
                                            >
                                                Manual
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-500">{company.userCount}</td>
                                    <td className="px-4 py-3 text-sm text-slate-500">{company.noteCount}</td>
                                    <td className="px-4 py-3 text-right">
                                        <button
                                            onClick={() => handleEdit(company)}
                                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline mr-4"
                                        >
                                            Edit
                                        </button>
                                        {/* Removing a company whose users remain would leave them
                                            with no company, which means seeing everything. The API
                                            refuses that, so the button says so before it is used. */}
                                        <button
                                            onClick={() => handleRemove(company)}
                                            disabled={busy || company.userCount > 0}
                                            title={company.userCount > 0
                                                ? `${company.userCount} user(s) still belong to this company. Move them first, otherwise they would see every customer's release notes.`
                                                : undefined}
                                            className="text-sm text-red-500 hover:text-red-700 hover:underline disabled:text-slate-300 disabled:no-underline disabled:cursor-not-allowed"
                                        >
                                            Remove
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {companies.length === 0 && (
                        <div className="text-center py-8 text-slate-400 text-sm">
                            No companies yet. Everyone sees every release note until one is added
                            and assigned.
                            <span className="block mt-1">
                                Use <strong>Refresh from Jira</strong> above to build the list from
                                the Customer field.
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CompaniesDialog;
